"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useState, useRef, useEffect } from "react";
import { AuthButton } from "@/components/auth/AuthButton";

const UR_NAV_ITEMS = [
  { href: "/ur/recent", label: "最近の物件更新" },
  { href: "/ur/vacancy/count", label: "空室一覧" },
  { href: "/ur/analytics", label: "分析" },
];

const JKK_NAV_ITEMS = [
  { href: "/jkk/recent", label: "最近の物件更新" },
  { href: "/jkk/vacancy/count", label: "空室一覧" },
  { href: "/jkk/analytics", label: "分析" },
];

const BLOG_NAV_ITEMS = [
  { href: "/ur", label: "UR空室情報" },
  { href: "/jkk", label: "JKK空室情報" },
  { href: "/blog", label: "ブログ" },
];

const UR_AREA_ITEMS = [
  {
    region: "北海道・東北",
    prefectures: [
      { href: "/ur/area/hokkaitohoku/hokkaido", label: "北海道" },
      { href: "/ur/area/hokkaitohoku/miyagi", label: "宮城県" },
    ],
  },
  {
    region: "関東",
    prefectures: [
      { href: "/ur/area/kanto/tokyo", label: "東京都" },
      { href: "/ur/area/kanto/kanagawa", label: "神奈川県" },
      { href: "/ur/area/kanto/chiba", label: "千葉県" },
      { href: "/ur/area/kanto/saitama", label: "埼玉県" },
      { href: "/ur/area/kanto/ibaraki", label: "茨城県" },
    ],
  },
  {
    region: "東海",
    prefectures: [
      { href: "/ur/area/tokai/aichi", label: "愛知県" },
      { href: "/ur/area/tokai/mie", label: "三重県" },
      { href: "/ur/area/tokai/gifu", label: "岐阜県" },
    ],
  },
  {
    region: "関西",
    prefectures: [
      { href: "/ur/area/kansai/osaka", label: "大阪府" },
      { href: "/ur/area/kansai/hyogo", label: "兵庫県" },
      { href: "/ur/area/kansai/kyoto", label: "京都府" },
      { href: "/ur/area/kansai/shiga", label: "滋賀県" },
      { href: "/ur/area/kansai/nara", label: "奈良県" },
      { href: "/ur/area/kansai/wakayama", label: "和歌山県" },
    ],
  },
  {
    region: "中国",
    prefectures: [
      { href: "/ur/area/chugoku/okayama", label: "岡山県" },
      { href: "/ur/area/chugoku/hiroshima", label: "広島県" },
      { href: "/ur/area/chugoku/yamaguchi", label: "山口県" },
    ],
  },
  {
    region: "九州",
    prefectures: [{ href: "/ur/area/kyushu/fukuoka", label: "福岡県" }],
  },
];

const JKK_AREA_ITEMS = [
  {
    region: "23区",
    prefectures: [
      { href: "/jkk/area/chiyoda", label: "千代田区" },
      { href: "/jkk/area/chuo", label: "中央区" },
      { href: "/jkk/area/minato", label: "港区" },
      { href: "/jkk/area/shinjuku", label: "新宿区" },
      { href: "/jkk/area/bunkyo", label: "文京区" },
      { href: "/jkk/area/sumida", label: "墨田区" },
      { href: "/jkk/area/koto", label: "江東区" },
      { href: "/jkk/area/shinagawa", label: "品川区" },
      { href: "/jkk/area/meguro", label: "目黒区" },
      { href: "/jkk/area/ota", label: "大田区" },
      { href: "/jkk/area/setagaya", label: "世田谷区" },
      { href: "/jkk/area/shibuya", label: "渋谷区" },
      { href: "/jkk/area/nakano", label: "中野区" },
      { href: "/jkk/area/suginami", label: "杉並区" },
      { href: "/jkk/area/toshima", label: "豊島区" },
      { href: "/jkk/area/kita", label: "北区" },
      { href: "/jkk/area/arakawa", label: "荒川区" },
      { href: "/jkk/area/itabashi", label: "板橋区" },
      { href: "/jkk/area/nerima", label: "練馬区" },
      { href: "/jkk/area/adachi", label: "足立区" },
      { href: "/jkk/area/katsushika", label: "葛飾区" },
      { href: "/jkk/area/edogawa", label: "江戸川区" },
    ],
  },
  {
    region: "多摩地域",
    prefectures: [
      { href: "/jkk/area/hachioji", label: "八王子市" },
      { href: "/jkk/area/tachikawa", label: "立川市" },
      { href: "/jkk/area/musashino", label: "武蔵野市" },
      { href: "/jkk/area/mitaka", label: "三鷹市" },
      { href: "/jkk/area/fuchu", label: "府中市" },
      { href: "/jkk/area/chofu", label: "調布市" },
      { href: "/jkk/area/machida", label: "町田市" },
      { href: "/jkk/area/kodaira", label: "小平市" },
      { href: "/jkk/area/hino", label: "日野市" },
      { href: "/jkk/area/higashimurayama", label: "東村山市" },
      { href: "/jkk/area/kokubunji", label: "国分寺市" },
      { href: "/jkk/area/tama", label: "多摩市" },
      { href: "/jkk/area/inagi", label: "稲城市" },
      { href: "/jkk/area/nishitokyo", label: "西東京市" },
    ],
  },
];

/** redirectParam は認証画面の ?redirect=（(auth)/layout.tsx から渡す） */
export function Header({ redirectParam = "" }: { redirectParam?: string }) {
  const router = useRouter();
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [areaOpen, setAreaOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setAreaOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ページ遷移時にモバイルメニューを閉じる
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  // メニュー開閉時にbodyスクロールを制御
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  // JKKコンテキストの判定: パス、redirectパラメータ、localStorageの順に確認
  const isJkkPath = pathname.startsWith("/jkk");
  const isJkkRedirect = redirectParam.startsWith("/jkk");

  // /jkk または /ur ページを訪問したらlocalStorageに記録
  useEffect(() => {
    if (pathname.startsWith("/jkk")) {
      localStorage.setItem("provider", "jkk");
    } else if (pathname.startsWith("/ur")) {
      localStorage.setItem("provider", "ur");
    }
  }, [pathname]);

  const [storedProvider, setStoredProvider] = useState<string | null>(null);
  useEffect(() => {
    setStoredProvider(localStorage.getItem("provider"));
  }, []);

  const isJkk = isJkkPath || isJkkRedirect || (!isJkkPath && !pathname.startsWith("/ur") && storedProvider === "jkk");
  const isBlog = pathname.startsWith("/blog") || pathname.startsWith("/guide") || pathname === "/";
  const navItems = isBlog ? BLOG_NAV_ITEMS : isJkk ? JKK_NAV_ITEMS : UR_NAV_ITEMS;
  const areaItems = isJkk ? JKK_AREA_ITEMS : UR_AREA_ITEMS;
  const homeHref = isBlog ? "/" : isJkk ? "/jkk" : "/ur";
  const searchHref = isJkk ? "/jkk/search" : "/ur/search";

  return (
    <header className="bg-navy-dark text-white sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
        <Link href={homeHref}>
          <img
            src={isBlog ? "/logo_kuushitsu.svg" : isJkk ? "/logo_jkk.svg" : "/logo_ur.svg"}
            alt="空室情報.jp"
            className="h-6 md:h-8"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-6">
          {navItems.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className="text-sm text-gray-300 hover:text-white transition-colors"
            >
              {item.label}
            </Link>
          ))}

          {/* Search field with area dropdown (hidden on blog pages) */}
          {!isBlog && (
            <div className="relative" ref={searchRef}>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (searchQuery.trim()) {
                    router.push(`${searchHref}?q=${encodeURIComponent(searchQuery.trim())}`);
                    setSearchQuery("");
                    setAreaOpen(false);
                  }
                }}
              >
                <input
                  type="text"
                  placeholder="物件名・住所・エリア"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setAreaOpen(true)}
                  className="border border-gray-600 bg-transparent rounded-full pl-4 pr-9 py-1.5 text-sm text-white placeholder-gray-400 w-64 focus:outline-none focus:border-accent"
                />
                <button type="submit" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </button>
              </form>

              {areaOpen && !searchQuery && (
                <div className={`absolute top-full right-0 mt-2 bg-white text-foreground rounded-lg border border-border p-4 grid gap-4 shadow-lg ${isJkk ? "w-96 grid-cols-2" : "w-80 grid-cols-2"}`}>
                  {areaItems.map((area) => (
                    <div key={area.region}>
                      <h3 className="font-semibold text-sm text-muted mb-1">
                        {area.region}
                      </h3>
                      <ul className="space-y-1">
                        {area.prefectures.map((pref) => (
                          <li key={pref.href}>
                            <Link
                              href={pref.href}
                              className="text-sm text-foreground hover:text-accent transition-colors"
                              onClick={() => setAreaOpen(false)}
                            >
                              {pref.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <AuthButton />
        </nav>

        {/* Mobile hamburger */}
        <button
          className="md:hidden p-2"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="メニュー"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            {mobileOpen ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <nav className="md:hidden bg-navy border-t border-gray-700 px-5 py-5 space-y-5 overflow-y-auto" style={{ maxHeight: "calc(100dvh - 64px)" }}>
          {/* Auth button */}
          <AuthButton mobile />

          {navItems.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className="block text-gray-300 hover:text-white py-1 text-base"
              onClick={() => setMobileOpen(false)}
            >
              {item.label}
            </Link>
          ))}

          {/* Area links + search (hidden on blog pages) */}
          {!isBlog && (
            <div className="pt-2 border-t border-gray-700">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (searchQuery.trim()) {
                    router.push(`${searchHref}?q=${encodeURIComponent(searchQuery.trim())}`);
                    setSearchQuery("");
                    setMobileOpen(false);
                  }
                }}
                className="relative mb-3"
              >
                <input
                  type="text"
                  placeholder="物件名・住所・交通"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full border border-gray-600 bg-transparent rounded-full pl-4 pr-9 py-2.5 text-sm text-white placeholder-gray-400 focus:outline-none focus:border-accent"
                />
                <button type="submit" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </button>
              </form>
              <p className="text-xs text-gray-500 mb-2">エリア</p>
              {areaItems.map((area) => (
                <div key={area.region} className="mb-4">
                  <p className="text-xs text-gray-400 font-semibold mb-1.5">
                    {area.region}
                  </p>
                  <div className="flex flex-wrap gap-x-3 gap-y-2">
                    {area.prefectures.map((pref) => (
                      <Link
                        key={pref.href}
                        href={pref.href}
                        className="text-sm text-gray-300 hover:text-white"
                        onClick={() => setMobileOpen(false)}
                      >
                        {pref.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </nav>
      )}
    </header>
  );
}
